"use client"

import { useEffect, useMemo, useState, useLayoutEffect, useRef } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { Loader2, Shuffle } from "lucide-react"
import { useTranslations } from "next-intl"
import { Icon } from "@/shared/components/icons"
import { cn } from "@/shared/lib/utils"
import { type GenerationPromptModifierKey, useGenerationStore } from "../store"
import { usePromptModifierOptions } from "../hooks/use-prompt-modifier-options"
import { PROMPT_MAX_LENGTH, GENERATION_COST_PER_IMAGE } from "../constants"
import { ControlPills, GenerationSettingsMenu } from "./control-pills"
import { AdvancedSettings } from "./advanced-settings"
import { TosViolationBanner } from "./tos-violation-banner"

const PROMPT_TEXTAREA_MAX_PX = 96
const ALL_OPTION_GROUP = "__all__"

function syncPromptTextareaHeight(el: HTMLTextAreaElement) {
  el.style.height = "auto"
  const next = Math.min(el.scrollHeight, PROMPT_TEXTAREA_MAX_PX)
  el.style.height = `${next}px`
}

type ModifierOption = {
  label: string
  group: string
}

type ModifierCard = {
  key: GenerationPromptModifierKey
  label: string
  toneClassName: string
  options: ModifierOption[]
}

// Text-first cards (no per-category icons; thumbnails come later once option
// preview images exist). Real option lists load from public.prompt_modifiers
// (LoRA-backed options only); the few options below are loading fallbacks.
// race/age/eyeColor cards are deferred — their DB rows are inactive, so they
// stay hidden; re-enable by reactivating the rows and re-adding a card here.
const PROMPT_MODIFIER_CARDS: ModifierCard[] = [
  {
    key: "position",
    label: "Position",
    toneClassName: "from-primary/85 via-rose-500/55 to-white/10",
    options: [
      { label: "POV Missionary", group: "Positions" },
      { label: "Mating Press", group: "Positions" },
      { label: "Cowgirl (3rd Person)", group: "Positions" },
      { label: "Pronebone", group: "Positions" },
      { label: "Spooning Sex", group: "Positions" },
      { label: "POV Blowjob", group: "Oral" },
    ],
  },
  {
    key: "outfit",
    label: "Outfit",
    toneClassName: "from-fuchsia-400/80 via-purple-500/45 to-white/10",
    options: [
      { label: "Lingerie", group: "Lingerie & Swim" },
      { label: "Micro Bikini", group: "Lingerie & Swim" },
      { label: "Latex suit", group: "Fetish" },
      { label: "Maid", group: "Costume & Roleplay" },
    ],
  },
  {
    key: "location",
    label: "Location",
    toneClassName: "from-teal-400/80 via-emerald-500/40 to-white/10",
    options: [
      { label: "Bedroom", group: "Indoors" },
      { label: "Beach", group: "Outdoors" },
      { label: "Onsen", group: "Indoors" },
      { label: "Rooftop", group: "Outdoors" },
    ],
  },
  {
    key: "bodyType",
    label: "Body Type",
    toneClassName: "from-[#FF6B9D]/80 via-amber-400/45 to-white/10",
    options: [
      { label: "BBW", group: "Shape" },
      { label: "Fat", group: "Shape" },
      { label: "Fit", group: "Shape" },
      { label: "Muscular", group: "Shape" },
      { label: "Skinny", group: "Shape" },
      { label: "Thick", group: "Shape" },
    ],
  },
  {
    key: "breasts",
    label: "Breasts",
    toneClassName: "from-orange-400/80 via-rose-400/45 to-white/10",
    options: [
      { label: "Large Breast", group: "Breasts" },
      { label: "Natural Breast", group: "Breasts" },
      { label: "Small Breasts", group: "Breasts" },
      { label: "Large Ass", group: "Ass" },
    ],
  },
  {
    key: "facialExpression",
    label: "Expression",
    toneClassName: "from-zinc-200/75 via-zinc-500/45 to-white/10",
    options: [
      { label: "Happy", group: "Smile" },
      { label: "Smirk", group: "Flirty" },
      { label: "Tongue Out", group: "Flirty" },
      { label: "Wink", group: "Flirty" },
      { label: "Embarrassed", group: "Soft" },
      { label: "Sad", group: "Mood" },
    ],
  },
  {
    key: "hairStyle",
    label: "Hair",
    toneClassName: "from-sky-400/80 via-indigo-500/45 to-white/10",
    options: [
      { label: "Ponytail", group: "Style" },
      { label: "Braided", group: "Style" },
      { label: "Curly", group: "Style" },
      { label: "Hair bun", group: "Style" },
    ],
  },
]

const SHUFFLE_PROMPTS = [
  "ultra realistic adult couple in a private hotel suite, warm night lighting, cinematic close-up, intimate mood",
  "beautiful adult model in a luxury bedroom, candlelight, soft skin texture, editorial photo, shallow depth of field",
  "adult fantasy scene on a rooftop at night, neon glow, full body framing, dramatic shadows, high detail",
  "adult boudoir photo in a marble bathroom, rain on windows, 85mm portrait lens, realistic skin, soft expression",
  "anime adult woman in a private studio set, red light atmosphere, dynamic pose, glossy detailed illustration",
  "adult couple by a poolside at sunset, warm summer atmosphere, wide shot, cinematic color grading",
]

function randomPrompt(currentPrompt: string): string {
  const available = SHUFFLE_PROMPTS.filter((item) => item !== currentPrompt.trim())
  const source = available.length > 0 ? available : SHUFFLE_PROMPTS
  return source[Math.floor(Math.random() * source.length)]
}

interface PromptBarProps {
  onGenerate: () => void
  isPending: boolean
  tosViolation?: string | null
  onDismissTos?: () => void
}

export function PromptBar({ onGenerate, isPending, tosViolation, onDismissTos }: PromptBarProps) {
  const t = useTranslations("generate")
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [activeModifier, setActiveModifier] = useState<GenerationPromptModifierKey | null>(null)
  const [activeGroup, setActiveGroup] = useState(ALL_OPTION_GROUP)
  const [optionSearch, setOptionSearch] = useState("")
  const prompt = useGenerationStore((s) => s.prompt)
  const setPrompt = useGenerationStore((s) => s.setPrompt)
  const numImages = useGenerationStore((s) => s.numImages)
  const showAdvanced = useGenerationStore((s) => s.showAdvanced)
  const promptModifiers = useGenerationStore((s) => s.promptModifiers)
  const setPromptModifier = useGenerationStore((s) => s.setPromptModifier)
  const totalCost = numImages * GENERATION_COST_PER_IMAGE
  const hasPrompt = Boolean(prompt.trim())
  const canGenerate = hasPrompt && !isPending
  const showGenerateAsActive = hasPrompt || isPending
  const { options: dbOptions } = usePromptModifierOptions()

  // DB-backed options (only LoRA-backed ones are active server-side). While
  // loading or on error dbOptions is undefined → static defaults. A loaded
  // empty list means the category has no LoRA yet → hide its card.
  const modifierCards = useMemo(() => {
    const modifierLabels: Partial<Record<GenerationPromptModifierKey, string>> = {
      position: t("modifiers.position"),
      outfit: t("modifiers.outfit"),
      location: t("modifiers.location"),
      bodyType: t("modifiers.bodyType"),
      breasts: t("modifiers.breasts"),
      facialExpression: t("modifiers.expression"),
      hairStyle: t("modifiers.hair"),
    }

    return PROMPT_MODIFIER_CARDS.map((card) => ({
      ...card,
      label: modifierLabels[card.key] ?? card.label,
      options: dbOptions?.[card.key] ?? card.options,
    })).filter((card) => card.options.length > 0)
  }, [dbOptions, t])
  const activeCard = modifierCards.find((card) => card.key === activeModifier)

  const optionGroups = useMemo(() => {
    if (!activeCard) return [ALL_OPTION_GROUP]
    return [ALL_OPTION_GROUP, ...Array.from(new Set(activeCard.options.map((option) => option.group)))]
  }, [activeCard])

  const filteredOptions = useMemo(() => {
    if (!activeCard) return []
    const search = optionSearch.trim().toLowerCase()
    return activeCard.options.filter((option) => {
      const matchesGroup = activeGroup === ALL_OPTION_GROUP || option.group === activeGroup
      const matchesSearch =
        !search ||
        option.label.toLowerCase().includes(search) ||
        option.group.toLowerCase().includes(search)
      return matchesGroup && matchesSearch
    })
  }, [activeCard, activeGroup, optionSearch])

  useLayoutEffect(() => {
    if (textareaRef.current) {
      syncPromptTextareaHeight(textareaRef.current)
    }
  }, [prompt])

  const openModifierPicker = (key: GenerationPromptModifierKey) => {
    setActiveModifier(key)
    setActiveGroup(ALL_OPTION_GROUP)
    setOptionSearch("")
  }

  const closeModifierPicker = () => {
    setActiveModifier(null)
    setActiveGroup(ALL_OPTION_GROUP)
    setOptionSearch("")
  }

  useEffect(() => {
    if (!activeModifier) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      setActiveModifier(null)
      setActiveGroup(ALL_OPTION_GROUP)
      setOptionSearch("")
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [activeModifier])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !isPending && prompt.trim()) {
      e.preventDefault()
      onGenerate()
    }
  }

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value.slice(0, PROMPT_MAX_LENGTH)
    setPrompt(value)
    syncPromptTextareaHeight(e.target)
  }

  const handleShufflePrompt = () => {
    setPrompt(randomPrompt(prompt).slice(0, PROMPT_MAX_LENGTH))
    window.requestAnimationFrame(() => {
      if (textareaRef.current) syncPromptTextareaHeight(textareaRef.current)
    })
  }

  return (
    <div className="w-full max-w-3xl mx-auto space-y-0">
      {tosViolation && onDismissTos && (
        <TosViolationBanner
          message={tosViolation}
          onDismiss={onDismissTos}
        />
      )}

      <div className="relative pt-12">
        <div className="no-scrollbar absolute inset-x-0 top-0 z-20 flex overflow-x-auto px-5 pb-3">
          <div className="flex min-w-max items-start">
            {modifierCards.map((card, index) => {
              const value = promptModifiers[card.key]
              const isSelected = Boolean(value)

              return (
                <motion.button
                  key={card.key}
                  type="button"
                  onClick={() => openModifierPicker(card.key)}
                  disabled={isPending}
                  initial={false}
                  animate={{
                    scale: isSelected ? 1.04 : 1,
                    rotate: isSelected ? -2 : 3,
                    y: isSelected ? -4 : 0,
                  }}
                  whileHover={{ scale: 1.07, y: -7 }}
                  whileTap={{ scale: 1.12 }}
                  transition={{ type: "spring", stiffness: 360, damping: 24 }}
                  className={cn(
                    "group relative flex h-[92px] w-[74px] shrink-0 flex-col justify-between overflow-hidden rounded-lg border p-2 text-left shadow-[0_10px_26px_rgba(0,0,0,0.42)] disabled:opacity-50",
                    index > 0 && "-ml-4",
                    isSelected
                      ? "z-20 border-white/[0.24] bg-white/[0.12]"
                      : "z-10 border-white/[0.1] bg-zinc-800 hover:border-white/[0.18] hover:bg-zinc-700"
                  )}
                  aria-label={`${card.label}: ${value ?? t("noSelection")}`}
                >
                  <span
                    className={cn(
                      "absolute inset-0 bg-gradient-to-br transition-opacity",
                      card.toneClassName,
                      isSelected ? "opacity-100" : "opacity-35 group-hover:opacity-55"
                    )}
                    aria-hidden
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-black/72 via-black/10 to-white/[0.08]" />
                  {isSelected ? (
                    <span
                      className="relative flex size-6 items-center justify-center rounded-full bg-white text-black shadow-[0_2px_8px_rgba(0,0,0,0.45)]"
                      aria-hidden
                    >
                      <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M2.5 6.2L4.8 8.5L9.5 3.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  ) : (
                    <span className="relative flex items-center gap-1.5" aria-hidden>
                      <span className="flex size-6 items-center justify-center rounded-full border border-dashed border-white/[0.35] text-[13px] font-medium leading-none text-white/70 transition-colors group-hover:border-white/[0.55] group-hover:text-white">
                        +
                      </span>
                      <span className="flex items-center gap-[3px]">
                        <span className="size-[3px] rounded-full bg-white/[0.28]" />
                        <span className="size-[3px] rounded-full bg-white/[0.20]" />
                        <span className="size-[3px] rounded-full bg-white/[0.14]" />
                      </span>
                    </span>
                  )}
                  <span className="relative min-w-0">
                    <span className="block truncate text-[10px] font-semibold uppercase tracking-[0.04em] text-white/58">
                      {card.label}
                    </span>
                    {isSelected ? (
                      <span className="mt-0.5 block truncate text-[11px] font-bold leading-tight text-white">
                        {value}
                      </span>
                    ) : (
                      <span className="mt-0.5 block truncate text-[11px] font-medium leading-tight text-white/45">
                        {t("tapToPick")}
                      </span>
                    )}
                  </span>
                </motion.button>
              )
            })}
          </div>
        </div>

        <div
          className={cn(
            "overflow-hidden rounded-3xl border px-4 pb-3 pt-11 shadow-[0_18px_48px_rgba(0,0,0,0.48)] backdrop-blur-xl transition-colors",
            "bg-card/78",
            tosViolation
              ? "border-red-500/40 shadow-red-950/35"
              : "border-white/[0.1]"
          )}
        >
          <div className="mb-3 min-w-0">
            <textarea
              ref={textareaRef}
              placeholder={t("promptPlaceholder")}
              value={prompt}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              disabled={isPending}
              rows={1}
              className={cn(
                "box-border min-h-0 w-full resize-none border-0 bg-transparent text-[16px] leading-snug sm:text-sm",
                "text-zinc-100 placeholder:text-zinc-500",
                "px-0 py-1.5 focus:outline-none focus:ring-0 disabled:opacity-50",
                "max-h-[96px] overflow-y-auto [scrollbar-width:thin]"
              )}
              aria-label={t("imagePromptLabel")}
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2.5">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <motion.button
                type="button"
                onClick={handleShufflePrompt}
                disabled={isPending}
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 1.15, rotate: 10 }}
                transition={{ type: "spring", stiffness: 420, damping: 20 }}
                className="flex size-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.06] text-zinc-300 transition-colors hover:border-white/[0.16] hover:bg-white/[0.1] hover:text-white disabled:opacity-50"
                aria-label={t("shufflePrompt")}
                title={t("shufflePrompt")}
              >
                <Shuffle className="size-[15px]" />
              </motion.button>
              <ControlPills />
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="text-[10px] font-medium tabular-nums text-zinc-500">
                {t("coins", { count: totalCost })}
              </span>
              <GenerationSettingsMenu title={t("setting")} />
              <motion.button
                type="button"
                onClick={onGenerate}
                disabled={!canGenerate}
                whileHover={canGenerate ? { scale: 1.04, y: -1 } : undefined}
                whileTap={canGenerate ? { scale: 0.96 } : undefined}
                transition={{ type: "spring", stiffness: 420, damping: 22 }}
                className={cn(
                  "group relative flex h-10 shrink-0 items-center justify-center gap-2 overflow-hidden rounded-full border px-3.5 text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-[#1d7df2]/45",
                  showGenerateAsActive
                    ? "border-[#5aa7ff]/45 bg-[#1d7df2] text-white shadow-[0_0_26px_rgba(29,125,242,0.34)] hover:border-[#8fc2ff]/60 hover:bg-[#1669d6]"
                    : "cursor-not-allowed border-white/[0.08] bg-white/[0.06] text-white/38"
                )}
                aria-label={t("generateImages")}
              >
                <span
                  className="absolute inset-0 bg-white/[0.12] opacity-0 transition-opacity group-hover:opacity-100"
                  aria-hidden="true"
                />
                {isPending ? (
                  <Loader2 className="relative size-[16px] animate-spin" />
                ) : null}
                <span className="relative">
                  {isPending ? t("generating") : t("generate")}
                </span>
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {showAdvanced && (
        <div className="w-full max-w-3xl mx-auto">
          <AdvancedSettings />
        </div>
      )}

      <AnimatePresence>
        {activeCard && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/72 px-3 py-4 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModifierPicker}
          >
              <motion.div
                role="dialog"
                aria-modal="true"
                aria-label={t("optionsAria", { label: activeCard.label })}
              className="grid h-[min(720px,calc(100dvh-32px))] w-full max-w-6xl grid-cols-1 overflow-hidden rounded-lg border border-white/[0.1] bg-[#101010] shadow-[0_24px_90px_rgba(0,0,0,0.7)] lg:grid-cols-[1fr_320px]"
              initial={{ opacity: 0, scale: 0.96, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 14 }}
              transition={{ type: "spring", stiffness: 260, damping: 28 }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex min-h-0 flex-col">
                <div className="flex items-start justify-between gap-3 border-b border-white/[0.06] px-5 py-4">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white">{activeCard.label}</p>
                    <p className="mt-1 truncate text-xs text-zinc-500">
                      {promptModifiers[activeCard.key] ?? t("noSelection")}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {promptModifiers[activeCard.key] && (
                      <button
                        type="button"
                        onClick={() => {
                          setPromptModifier(activeCard.key, null)
                          closeModifierPicker()
                        }}
                        className="h-9 rounded-full border border-white/[0.08] bg-white/[0.04] px-3.5 text-xs font-semibold text-zinc-300 transition-colors hover:bg-white/[0.08] hover:text-white lg:hidden"
                      >
                        {t("clear", { label: "" }).trim()}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={closeModifierPicker}
                      className="flex size-9 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.04] text-zinc-300 transition-colors hover:bg-white/[0.08] hover:text-white"
                      aria-label={t("closeSelector")}
                    >
                      <Icon name="x" size={15} />
                    </button>
                  </div>
                </div>

                <div className="no-scrollbar flex gap-2 overflow-x-auto px-5 pt-4">
                  {optionGroups.map((group) => (
                    <button
                      key={group}
                      type="button"
                      onClick={() => setActiveGroup(group)}
                      className={cn(
                        "h-9 shrink-0 rounded-full px-4 text-sm font-semibold transition-colors",
                        activeGroup === group
                          ? "bg-white text-black"
                          : "bg-white/[0.08] text-zinc-300 hover:bg-white/[0.12] hover:text-white"
                      )}
                    >
                      {group === ALL_OPTION_GROUP ? t("all") : group}
                    </button>
                  ))}
                </div>

                <div className="relative mx-5 mt-4">
                  <Icon
                    name="search"
                    size={15}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
                  />
                  <input
                    value={optionSearch}
                    onChange={(event) => setOptionSearch(event.target.value)}
                    placeholder={t("search", { label: activeCard.label.toLowerCase() })}
                    className="h-11 w-full rounded-full border border-white/[0.08] bg-white/[0.06] pl-9 pr-4 text-[16px] text-white outline-none transition-colors placeholder:text-zinc-500 focus:border-white/[0.18] focus:bg-white/[0.08] sm:text-sm"
                  />
                </div>

                {promptModifiers[activeCard.key] && (
                  <div className="px-5 pt-3 lg:hidden">
                    <button
                      type="button"
                      onClick={() => {
                        setPromptModifier(activeCard.key, null)
                        closeModifierPicker()
                      }}
                      className="h-10 w-full rounded-full border border-white/[0.08] text-sm font-semibold text-zinc-300 transition-colors hover:bg-white/[0.06] hover:text-white"
                    >
                      {t("clear", { label: activeCard.label })}
                    </button>
                  </div>
                )}

                <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto p-5">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                    {filteredOptions.map((option, index) => {
                      const isCurrent = promptModifiers[activeCard.key] === option.label

                      return (
                        <motion.button
                          key={option.label}
                          type="button"
                          onClick={() => {
                            setPromptModifier(activeCard.key, option.label)
                            closeModifierPicker()
                          }}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: Math.min(index * 0.015, 0.18) }}
                          whileHover={{ scale: 1.02, y: -2 }}
                          whileTap={{ scale: 0.98 }}
                          className={cn(
                            "group relative flex min-h-[72px] flex-col justify-between overflow-hidden rounded-xl border p-3 text-left transition-colors",
                            isCurrent
                              ? "border-white/[0.35] bg-white/[0.09]"
                              : "border-white/[0.07] bg-white/[0.035] hover:border-white/[0.16] hover:bg-white/[0.06]"
                          )}
                        >
                          <span
                            className={cn(
                              "pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r opacity-70",
                              activeCard.toneClassName
                            )}
                            aria-hidden
                          />
                          <span className="flex items-center justify-between gap-2">
                            <span className="flex items-center gap-1.5 min-w-0">
                              <span
                                className={cn(
                                  "size-[6px] shrink-0 rounded-full bg-gradient-to-br",
                                  activeCard.toneClassName
                                )}
                                aria-hidden
                              />
                              <span className="truncate text-[10px] font-medium uppercase tracking-[0.08em] text-zinc-500">
                                {option.group}
                              </span>
                            </span>
                            {isCurrent && (
                              <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-white text-black" aria-hidden>
                                <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                                  <path d="M2.5 6.2L4.8 8.5L9.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </span>
                            )}
                          </span>
                          <span className="mt-2 line-clamp-2 text-sm font-semibold leading-snug text-white">
                            {option.label}
                          </span>
                        </motion.button>
                      )
                    })}
                  </div>
                </div>
              </div>

              <aside className="hidden border-l border-white/[0.06] bg-black/18 p-5 lg:flex lg:flex-col">
                <div className="flex min-h-0 flex-1 items-center justify-center rounded-lg border border-white/[0.08] bg-black/20 p-6 text-center">
                  <div className="min-w-0">
                    <span
                      className={cn(
                        "mx-auto block size-11 rounded-full bg-gradient-to-br opacity-90 shadow-[0_6px_22px_rgba(0,0,0,0.4)]",
                        activeCard.toneClassName
                      )}
                      aria-hidden
                    />
                    <p className="mt-4 text-sm font-semibold text-white">
                      {promptModifiers[activeCard.key] ?? activeCard.label}
                    </p>
                    <p className="mt-1 text-xs text-zinc-500">
                      {promptModifiers[activeCard.key]
                        ? t("selected")
                        : t("nothingSelected")}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setPromptModifier(activeCard.key, null)
                    closeModifierPicker()
                  }}
                  className="mt-4 h-10 rounded-full border border-white/[0.08] text-sm font-semibold text-zinc-300 transition-colors hover:bg-white/[0.06] hover:text-white"
                >
                  {t("clear", { label: activeCard.label })}
                </button>
              </aside>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
